import { registerSW } from 'virtual:pwa-register';

/**
 * The service worker registration, owned by boot rather than by a screen.
 *
 * It used to live inside `useServiceWorkerUpdate`, which is called by
 * `UpdatePrompt`, which is rendered by exactly one page — Profil. So the
 * service worker only ever registered for a worker who opened the settings
 * screen: everyone else ran with no precached shell, no offline navigation
 * fallback, and no `beforeinstallprompt` (Chrome fires it only once a service
 * worker controlling `start_url` is registered). "Add to home screen" was
 * therefore invisible for the entire app except one tab, and the offline shell
 * the punch queue is built on was never installed at all.
 *
 * Registration is a boot concern, so it is done from `app/bootstrap.ts` and
 * the resulting state is published here for whoever wants to render it. One
 * registration, one Workbox instance, any number of subscribers.
 */
interface ServiceWorkerState {
  /** A new build is waiting. Show the prompt; never reload on your own. */
  readonly updateAvailable: boolean;
  /** The shell is cached and the PWA now works with no network. */
  readonly offlineReady: boolean;
}

const IDLE: ServiceWorkerState = { updateAvailable: false, offlineReady: false };

let state: ServiceWorkerState = IDLE;
let updateSW: ((reload?: boolean) => Promise<void>) | null = null;
const listeners = new Set<() => void>();

function publish(next: ServiceWorkerState): void {
  state = next;
  for (const listener of listeners) listener();
}

/**
 * Idempotent: a second call is a no-op, so a hot reload or a double boot
 * cannot end up with two Workbox instances racing to report the same waiting
 * worker.
 */
export function registerServiceWorker(): void {
  if (updateSW !== null) return;
  if (!('serviceWorker' in navigator)) return;

  updateSW = registerSW({
    immediate: true,
    onRegisteredSW: (_url, registration) => {
      if (registration === undefined) return;
      startUpdateChecks(registration);
    },
    onNeedRefresh: () => {
      publish({ ...state, updateAvailable: true });
    },
    onOfflineReady: () => {
      publish({ ...state, offlineReady: true });
    },
  });
}

const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;

/**
 * Why the app has to go looking for deploys.
 *
 * The browser only checks `sw.js` on a navigation, and an installed app that
 * stays open through a shift never navigates. So a deploy at 14:00 would sit
 * unseen until the worker relaunched the app. Checking whenever the app comes
 * back to the foreground — a phone app is backgrounded and foregrounded all
 * night — plus once an hour while it stays visible is enough; a found build
 * still only waits for « Recharger », never reloads by itself.
 *
 * `update()` rejects when offline; that is not an error worth surfacing.
 */
function startUpdateChecks(registration: ServiceWorkerRegistration): void {
  const check = (): void => {
    void registration.update().catch(() => undefined);
  };
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') check();
  });
  setInterval(check, UPDATE_CHECK_INTERVAL_MS);
}

export function subscribeServiceWorker(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Stable reference between publishes — `useSyncExternalStore` requires it. */
export function getServiceWorkerState(): ServiceWorkerState {
  return state;
}

/** Activates the waiting worker and reloads. Caller decides when. */
export async function applyServiceWorkerUpdate(): Promise<void> {
  await updateSW?.(true);
}

export function dismissServiceWorkerNotice(): void {
  publish(IDLE);
}
